import React, { useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Fade from '@mui/material/Fade';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { ActiveToolCall } from '../../pages/askFreya/types';
import type { ToolCategory } from '../../../../infra/api/askFreya/types';

const CATEGORY_EMOJI: Record<ToolCategory, string> = {
  retrieval: '🔍',
  analysis: '📊',
  mutation: '✏️',
  delegation: '🤖',
  validation: '✅',
  display: '🎨',
  default: '⚙️',
};

const MAX_VISIBLE = 5;
const THINKING_PREVIEW_LENGTH = 100;

interface ActivityFeedProps {
  toolCalls: ActiveToolCall[];
  thinkingContent?: string;
  isCollapsible?: boolean;
  defaultCollapsed?: boolean;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({ toolCalls, thinkingContent, isCollapsible = false, defaultCollapsed = false }) => {
  const [thinkingExpanded, setThinkingExpanded] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const visibleCalls = toolCalls.slice(-MAX_VISIBLE);
  const hasThinking = thinkingContent && thinkingContent.length > 0;
  const toolCount = toolCalls.length;

  if (isCollapsible) {
    return (
      <div className='flex flex-col text-14 mt-4 mb-8'>
        <button
          type='button'
          onClick={() => setCollapsed((prev) => !prev)}
          className='flex items-center gap-6 text-12 text-neutral-50 hover:text-neutral-70 transition-colors cursor-pointer bg-transparent border-none p-0'
        >
          {collapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
          <span>
            ⚡ {toolCount} tool{toolCount !== 1 ? 's' : ''} used
          </span>
        </button>
        <Collapse in={!collapsed} timeout={200}>
          <div className='flex flex-col gap-4 mt-4'>
            {visibleCalls.map((tc) => (
              <ToolCallItem key={tc.tool_call_id} toolCall={tc} />
            ))}
            {hasThinking && (
              <ThinkingSection thinkingContent={thinkingContent} expanded={thinkingExpanded} onToggle={() => setThinkingExpanded((prev) => !prev)} />
            )}
          </div>
        </Collapse>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4 text-14 mt-4'>
      {visibleCalls.map((tc) => (
        <Fade key={tc.tool_call_id} in timeout={200}>
          <div>
            <ToolCallItem toolCall={tc} />
          </div>
        </Fade>
      ))}
      {hasThinking && <ThinkingSection thinkingContent={thinkingContent} expanded={thinkingExpanded} onToggle={() => setThinkingExpanded((prev) => !prev)} />}
    </div>
  );
};

interface ToolCallItemProps {
  toolCall: ActiveToolCall;
}

const ToolCallItem: React.FC<ToolCallItemProps> = ({ toolCall }) => {
  const isRunning = toolCall.status === 'running';
  return (
    <div className='flex flex-col gap-1'>
      <div className={`flex items-center gap-8 py-2 ${isRunning ? 'text-neutral-70' : 'text-neutral-40 opacity-60'}`}>
        <span className='text-14'>{CATEGORY_EMOJI[toolCall.category] ?? CATEGORY_EMOJI.default}</span>
        <span className='truncate'>{toolCall.display_name}</span>
        {isRunning ? (
          <CircularProgress size={14} thickness={5} sx={{ color: 'var(--color-primary)' }} />
        ) : (
          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'var(--color-neutral-40)' }} />
        )}
      </div>
      {toolCall.description && <span className='text-12 text-neutral-40 truncate ml-28'>{toolCall.description}</span>}
    </div>
  );
};

interface ThinkingSectionProps {
  thinkingContent: string;
  expanded: boolean;
  onToggle: () => void;
}

const ThinkingSection: React.FC<ThinkingSectionProps> = ({ thinkingContent, expanded, onToggle }) => (
  <div className='mt-4'>
    <button
      type='button'
      onClick={onToggle}
      className='flex items-center gap-4 text-12 text-neutral-50 hover:text-neutral-70 transition-colors cursor-pointer bg-transparent border-none p-0'
    >
      {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
      <span className='italic'>
        {expanded ? 'Hide thinking' : `${thinkingContent.slice(0, THINKING_PREVIEW_LENGTH)}${thinkingContent.length > THINKING_PREVIEW_LENGTH ? '…' : ''}`}
      </span>
    </button>
    <Collapse in={expanded} timeout={200}>
      <div className='mt-4 p-8 bg-neutral-5 rounded-8 text-12 text-neutral-60 whitespace-pre-wrap max-h-[200px] overflow-y-auto'>{thinkingContent}</div>
    </Collapse>
  </div>
);
